/* eslint-disable import/no-extraneous-dependencies */
import React, {useEffect, useState} from 'react';
import axios from 'axios';
import {
  updateApartment,
  updateCity,
  updatePostalCode,
  updateSkipAddressStandardization,
  updateStateOrProvince,
  updateStreetAddress
} from '../../../../redux/app-state-slice';
import './ResidenceInfoSection.scss';
import CloseIconBlack from '../../../../assets/icons/close-grey.svg';
import states from '../../../../assets/states/USA.states.json';
import provinces from '../../../../assets/states/Canada.provinces.json';
import {DATA_FIELDS_ID} from '../../constants';
import {isValidStreetAddress} from '../../../../validation/PatternValidation';
import AddressTypeaheadService from '../../../../Services/AddressLookupService/AddressTypeaheadService';
import Input from '../../../../components/Input/Input';
import Select from '../../../../components/Select/Select';
import {maskPostalCodeOnType} from '../../../../validation/InputValidation';

const {STREET_ADDRESS, APARTMENT, CITY, POSTAL_CODE} = DATA_FIELDS_ID;

function ResidenceInfoSection({dispatch, customerData, appConfig}) {
  const [searchValue, setSearchValue] = useState('');
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [selectedValue, setSelectedValue] = useState(false);
  const stateData = states.map(({name, abbreviation}) => ({value: abbreviation, name}));
  const provinceData = provinces.map(({name, abbreviation}) => ({value: abbreviation, name}));
  const stateOrProvinceData = customerData.countryCode.value === 'CAN' ? provinceData : stateData;
  const stateOrProvinceLabel = customerData.countryCode.value === 'CAN' ? 'Province' : 'State';
  useEffect(() => {
    let cancel;
    let isMounted = true;
    const timerId = setTimeout(() => {
      const addressLookUp = async () => {
        if (isValidStreetAddress(searchValue, customerData.modalUpdateFlag) && isMounted) {
          try {
            const response = await AddressTypeaheadService(searchValue, customerData, appConfig, (c) => {
              cancel = c;
            });
            const lowerSearchValue = searchValue.toLowerCase();
            const filtered = response.data.filter((option) => option.streetAddress.toLowerCase().includes(lowerSearchValue));
            setSelectedValue(true);
            setFilteredOptions(filtered);
          } catch (e) {
            if (axios.isCancel(e)) return;
            setSelectedValue(false);
          }
        }
      };
      addressLookUp();
    }, 500);

    return () => {
      if (cancel instanceof Function) {
        cancel();
      }
      isMounted = false;
      clearTimeout(timerId);
    };
  }, [searchValue]);

  const handleSearchChange = (event) => {
    setSearchValue(event.target.value);
  };

  const handleDropdownSelection = (selectedIndex) => {
    const selectedItem = filteredOptions[selectedIndex];
    onSelect(selectedItem);
    setSelectedValue(false);
    dispatch(updateSkipAddressStandardization(true));
  };

  const onSelect = (item) => {
    dispatch(updateStreetAddress(item.streetAddress));
    if (!appConfig.modalUpdateFlag) dispatch(updateApartment(item.apartment));
    dispatch(updateCity(item.city));
    dispatch(updateStateOrProvince(item.stateOrProvince));
    dispatch(updatePostalCode(customerData.countryCode.value === 'CAN' ? maskPostalCodeOnType(item.postalCode) : item.postalCode));
  };

  const onClose = () => {
    setSelectedValue(false);
  };

  function showApartmentInTheDropdown(option) {
    if (!appConfig.modalUpdateFlag) {
      return option.apartment ? `, ${option.apartment}` : '';
    }
    return '';
  }

  return (
    <>
      <div data-testid='residence-info-section-fomo-container' id='prequal--web--residence-info-section'>
        <>
          <div className='prequal--web--bootstrap-styles'>
            <div className='prequal--web--input-container prequal--wc--input-container'>
              <div className='prequal--wc--input-tooltip-container'>
                <div className='form-floating'>
                  <Input
                    type='text'
                    id={STREET_ADDRESS}
                    maxLength='30'
                    onInputChangeHandler={(e) => {
                      dispatch(updateStreetAddress(e.target.value));
                      dispatch(updateApartment(customerData.apartment.value));
                    }}
                    onBlurChangeHandler={(e) => {
                      dispatch(updateStreetAddress(e.target.value.trim()));
                      dispatch(updateApartment(customerData.apartment.value.trim()));
                    }}
                    value={customerData.streetAddress.value || searchValue}
                    onChange={handleSearchChange}
                    name='address'
                    dataTestId={STREET_ADDRESS}
                    placeholder='Street Address'
                    inputHasError={customerData.streetAddress.errorMessage}
                  />
                  <label className='prequal--wc--prequal-form-label prequal--web--prequal-form-label-dark' htmlFor='streetAddress'>
                    {appConfig.modalUpdateFlag ? 'Full Address (Apt Number Optional)' : 'Street Address'}
                  </label>
                  {customerData.streetAddress.errorMessage && (
                    <p className='prequal--web--fomo-error-message'>{customerData.streetAddress.errorMessage}</p>
                  )}
                </div>
              </div>
              {selectedValue && filteredOptions.length > 0 && (
                <>
                  {appConfig.modalUpdateFlag ? (
                    <div className='prequal--web--addressSuggestions-container'>
                      <div className='prequal--web--dropdownHeader-modalUpdate'>
                        <span>Suggestions</span>
                        <button onClick={onClose} onKeyDown={onClose} id='closeButton' type='button'>
                          <img src={CloseIconBlack} alt='close' />
                        </button>
                      </div>
                      <div className='prequal--web--addressSuggestions-modalUpdate'>
                        {filteredOptions.map((option, index) => (
                          // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                          <div
                            data-testid={`prequal--web--address--${index}`}
                            className='prequal--web--addressItems'
                            // eslint-disable-next-line react/no-array-index-key
                            key={`${option.streetAddress}-${option.apartment}-${option.city}-${option.state}-${option.postalCode}`}
                            onMouseDown={() => handleDropdownSelection(index)}
                          >
                            <li className='prequal--web--addressText-modalUpdate'>
                              {option.streetAddress ? `${option.streetAddress}` : ''}
                              {showApartmentInTheDropdown(option)}
                              {option.city ? `, ${option.city}` : ''}
                              {option.stateOrProvince ? `, ${option.stateOrProvince}` : ''}
                              {option.postalCode ? `, ${option.postalCode}` : ''}
                            </li>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className='prequal--web--addressSuggestions'>
                      <li className='prequal--web--dropdownHeader'>
                        <span>Suggestions</span>
                        <button onClick={onClose} onKeyDown={onClose} id='closeButton' type='button'>
                          <img src={CloseIconBlack} alt='close' />
                        </button>
                      </li>
                      {filteredOptions.map((option, index) => (
                        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                        <div
                          data-testid={`prequal--web--address--${index}`}
                          className='prequal--web--addressItems'
                          // eslint-disable-next-line react/no-array-index-key
                          key={`${option.streetAddress}-${option.apartment}-${option.city}-${option.state}-${option.postalCode}`}
                          onMouseDown={() => handleDropdownSelection(index)}
                        >
                          <li className='prequal--web--addressText'>
                            {option.streetAddress ? `${option.streetAddress}` : ''}
                            {showApartmentInTheDropdown(option)}
                            {option.city ? `, ${option.city}` : ''}
                            {option.stateOrProvince ? `, ${option.stateOrProvince}` : ''}
                            {option.postalCode ? `, ${option.postalCode}` : ''}
                          </li>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
            {!appConfig.modalUpdateFlag ? (
              <div className='prequal--web--input-container prequal--wc--input-container'>
                <div className='prequal--wc--input-tooltip-container'>
                  <div className='form-floating'>
                    <Input
                      type='text'
                      maxLength='8'
                      onInputChangeHandler={(e) => dispatch(updateApartment(e.target.value))}
                      onBlurChangeHandler={(e) => dispatch(updateApartment(e.target.value.trim()))}
                      dataTestId={APARTMENT}
                      name={APARTMENT}
                      id={APARTMENT}
                      placeholder='Apt., Suite, Etc. (Optional)'
                      value={customerData.apartment.value}
                      inputHasError={customerData.apartment.errorMessage}
                    />
                    <label className='prequal--wc--prequal-form-label prequal--web--prequal-form-label-dark' htmlFor={APARTMENT}>
                      Apartment, Suite, Etc. (Optional)
                    </label>
                    {customerData.apartment.errorMessage && (
                      <p className='prequal--web--fomo-error-message'>{customerData.apartment.errorMessage}</p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              ''
            )}
            <div className='prequal--web--input-container prequal--wc--input-container'>
              <div className='prequal--wc--input-tooltip-container'>
                <div className='form-floating'>
                  <Input
                    type='text'
                    id={CITY}
                    maxLength='30'
                    onInputChangeHandler={(e) => dispatch(updateCity(e.target.value))}
                    onBlurChangeHandler={(e) => dispatch(updateCity(e.target.value.trim()))}
                    slot='input'
                    dataTestId={CITY}
                    name={CITY}
                    placeholder='City'
                    value={customerData.city.value}
                    autoComplete='address-level2'
                    inputHasError={customerData.city.errorMessage}
                  />
                  <label className='prequal--wc--prequal-form-label prequal--web--prequal-form-label-dark' htmlFor='city'>
                    City
                  </label>
                  {customerData.city.errorMessage && <p className='prequal--web--fomo-error-message'>{customerData.city.errorMessage}</p>}
                </div>
              </div>
            </div>
            <div className='prequal--wc--input-tooltip-container'>
              <div className='prequal--web--state-city-zip-section prequal--wc--state-city-zip-section'>
                <div className='prequal--web--input-container form-floating'>
                  <Select
                    placeholder={stateOrProvinceLabel}
                    name='stateOrProvince'
                    options={stateOrProvinceData}
                    value={customerData.stateOrProvince.value}
                    onChange={(e) => dispatch(updateStateOrProvince(e.target.value))}
                    hasLabel={false}
                    selectHasError={Boolean(customerData.stateOrProvince.errorMessage)}
                    id='stateOrProvince'
                  />
                  {customerData.stateOrProvince.errorMessage && (
                    <p className='prequal--web--fomo-error-message'>{customerData.stateOrProvince.errorMessage}</p>
                  )}
                </div>

                <div className='prequal--web--input-container'>
                  {customerData.countryCode.value === 'CAN' ? (
                    <div className='form-floating postal-code-padding'>
                      <Input
                        id={POSTAL_CODE}
                        maxLength='10'
                        onInputChangeHandler={(e) => dispatch(updatePostalCode(e.target.value))}
                        onBlurChangeHandler={(e) => dispatch(updatePostalCode(e.target.value.trim()))}
                        dataTestId={POSTAL_CODE}
                        slot='input'
                        name='postalCode'
                        placeholder='Postal Code'
                        value={customerData.postalCode.value}
                        autoComplete='postal-code'
                        inputHasError={customerData.postalCode.errorMessage}
                      />
                      <label
                        className='prequal--wc--prequal-form-label postal-code-label prequal--web--prequal-form-label-dark'
                        htmlFor='postalcode'
                      >
                        Postal Code
                      </label>
                      {customerData.postalCode.errorMessage && (
                        <p className='prequal--web--fomo-error-message'>{customerData.postalCode.errorMessage}</p>
                      )}
                    </div>
                  ) : (
                    <div className='form-floating postal-code-padding'>
                      <Input
                        id={POSTAL_CODE}
                        maxLength='10'
                        onInputChangeHandler={(e) => dispatch(updatePostalCode(e.target.value))}
                        onBlurChangeHandler={(e) => dispatch(updatePostalCode(e.target.value.trim()))}
                        dataTestId={POSTAL_CODE}
                        slot='input'
                        name='zipCode'
                        placeholder='ZIP Code'
                        value={customerData.postalCode.value}
                        autoComplete='postal-code'
                        inputHasError={customerData.postalCode.errorMessage}
                      />
                      <label
                        className='prequal--wc--prequal-form-label postal-code-label prequal--web--prequal-form-label-dark'
                        htmlFor='postalcode'
                      >
                        ZIP Code
                      </label>
                      {customerData.postalCode.errorMessage && (
                        <p className='prequal--web--fomo-error-message'>{customerData.postalCode.errorMessage}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      </div>
    </>
  );
}

export default ResidenceInfoSection;
